'use client';

import {
  FormControl,
  FormLabel,
  InputAdornment,
  SxProps,
  TextField,
  useTheme,
} from '@mui/material';
import { ChangeEvent, useEffect, useState } from 'react';

interface NumberInputFieldProps {
  label: string;
  value: number | string;
  onChange: (_val: number) => void;
  currency?: boolean;
  placeholder?: string;
  sx?: SxProps;
}

export function NumberInputField({
  label,
  value,
  onChange,
  currency = false,
  placeholder,
  sx,
}: NumberInputFieldProps) {
  const theme = useTheme();
  const [focused, setFocused] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>('');

  const formatCurrency = (val: number | string) =>
    new Intl.NumberFormat('en-AU').format(Number(val || 0));

  // Update display value when value prop changes (but not while focused)
  useEffect(() => {
    if (!focused) {
      if (value) {
        setDisplayValue(currency ? formatCurrency(value) : String(value));
      } else {
        setDisplayValue('');
      }
    }
  }, [value, currency, focused]);

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    // Remove all non-digit characters
    const raw = inputValue.replace(/[^\d]/g, '');

    // Update display value to show raw digits while typing
    setDisplayValue(raw);

    // Convert to number and notify parent
    onChange(Number(raw) || 0);
  };

  const handleFocus = () => {
    setFocused(true);
    // When focusing, show raw number without formatting
    if (value) {
      setDisplayValue(String(value).replace(/[^\d]/g, ''));
    } else {
      setDisplayValue('');
    }
  };

  const handleBlur = () => {
    setFocused(false);
    // When blurring, format the value if needed
    if (value) {
      setDisplayValue(currency ? formatCurrency(value) : String(value));
    } else {
      setDisplayValue('');
    }
  };

  return (
    <FormControl
      sx={{
        position: 'relative',
        ...sx,
      }}
    >
      <FormLabel
        sx={{
          mb: 0.75,
          fontSize: '0.8rem',
          fontWeight: 600,
          color: focused ? theme.palette.primary.main : 'text.primary',
          transition: 'color 0.2s ease',
        }}
      >
        {label}
      </FormLabel>

      <TextField
        size="small"
        type="text"
        value={displayValue}
        onChange={handleInput}
        placeholder={placeholder}
        onFocus={handleFocus}
        onBlur={handleBlur}
        InputProps={{
          startAdornment: currency ? (
            <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
              $
            </InputAdornment>
          ) : undefined,
          sx: {
            height: 44,
            borderRadius: '10px',
            backgroundColor: '#fff',
            '& fieldset': { borderColor: theme.palette.primary.main },
            '&:hover fieldset': { borderColor: theme.palette.primary.dark },
          },
        }}
      />
    </FormControl>
  );
}
